import { History, Pencil, UserMinus } from "lucide-react";

import type { RoleCatalog } from "@/api/access";
import { formatDateTime } from "@/lib/format";
import {
  ROLE_LABEL,
  UNKNOWN_ENTRA_USER,
  accessSummary,
  identityName,
  type SubjectAccessRow,
  type SubjectIdentity,
} from "@/lib/editorAccess";
import { Badge } from "@/ui/Badge";
import { IconButton } from "@/ui/IconButton";
import { Tooltip } from "@/ui/Tooltip";

import { SELF_ADMIN_LOCK_REASON } from "./GrantAccessDialog";

/**
 * ONE column track for the wide layout, shared by the header and every row.
 *
 * Gated on CONTAINER width, the `LibraryCatalog` rule: beside the sidebar
 * this card is narrower than the viewport, and a viewport breakpoint would
 * switch layouts at the wrong moment. Below `@3xl` every row stacks its cells
 * under their own labels, which cannot overflow at any width; at `@3xl` and
 * above the five tracks apply.
 */
const COLUMNS =
  "@3xl:grid @3xl:grid-cols-[minmax(0,2fr)_minmax(9rem,1fr)_minmax(0,1.6fr)_9.5rem_7.5rem] @3xl:items-start @3xl:gap-4";

const NARROW_LABEL =
  "block text-xs font-semibold uppercase tracking-wide text-muted-foreground @3xl:hidden";

/**
 * People with live Clinical Editor access — ONE row per Entra subject.
 *
 * Table SEMANTICS on a grid: `role="table"` / `row` / `columnheader` / `cell`
 * on plain elements, the responsive pattern this repository's catalogs use,
 * because a native `<table>` whose display is switched to stack its cells is
 * a table some browsers stop announcing as one. The column headers stay in
 * the accessibility tree at every width (visually hidden when the rows
 * stack), and the per-cell narrow labels are `aria-hidden` so nothing is
 * announced twice. One copy of each row in the DOM, at every width.
 *
 * There is deliberately NO GitHub column. The only GitHub identity the Editor
 * Service exposes is the CALLER's own publishing connection; no Admin-facing
 * API maps another subject to a GitHub login, and a column inferred from a
 * similar name or email would be exactly the false mapping the product rules
 * out. GitHub identity stays where the evidence is — PR authorship and review.
 */
export function AccessTable({
  rows,
  identityOf,
  catalog,
  self,
  onEdit,
  onRemove,
  onHistory,
}: {
  rows: readonly SubjectAccessRow[];
  identityOf: (subject: string) => SubjectIdentity;
  catalog: RoleCatalog | undefined;
  self: { subject: string };
  onEdit: (row: SubjectAccessRow) => void;
  onRemove: (row: SubjectAccessRow) => void;
  onHistory: (row: SubjectAccessRow) => void;
}) {
  return (
    <div
      role="table"
      aria-label="People with Clinical Editor access in this environment"
      className="@container"
    >
      <div role="rowgroup">
        <div
          role="row"
          className={`${COLUMNS} border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground @max-3xl:sr-only`}
        >
          <span role="columnheader">User</span>
          <span role="columnheader">Roles</span>
          <span role="columnheader">Access summary</span>
          <span role="columnheader">Last changed</span>
          <span role="columnheader" className="sr-only">
            Actions
          </span>
        </div>
      </div>
      <div role="rowgroup">
        {rows.map((row) => {
          const identity = identityOf(row.subject);
          const name = identityName(identity);
          const selfAdmin =
            row.subject === self.subject && row.roles.includes("admin");
          const removeLabel = `Remove Editor access for ${name}`;
          return (
            <div
              key={row.subject}
              role="row"
              data-subject={row.subject}
              className={`${COLUMNS} border-b border-border px-4 py-3 last:border-b-0 @max-3xl:space-y-3`}
            >
              <div role="cell" className="min-w-0">
                <span aria-hidden="true" className={NARROW_LABEL}>
                  User
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {identity.displayName ?? UNKNOWN_ENTRA_USER}
                  </span>
                  {identity.self ? <Badge tone="outline">You</Badge> : null}
                </span>
                {identity.email ? (
                  <span className="block text-xs text-muted-foreground">
                    {identity.email}
                  </span>
                ) : null}
                <span className="mt-0.5 block break-all text-xs text-muted-foreground">
                  <span className="sr-only">Entra Object ID </span>
                  <span className="select-all font-mono">{row.subject}</span>
                </span>
              </div>
              <div role="cell" className="min-w-0">
                <span aria-hidden="true" className={NARROW_LABEL}>
                  Roles
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {row.roles.map((role) => (
                    <Badge key={role} tone="neutral">
                      {ROLE_LABEL[role]}
                    </Badge>
                  ))}
                </span>
              </div>
              <div role="cell" className="min-w-0 text-sm text-muted-foreground">
                <span aria-hidden="true" className={NARROW_LABEL}>
                  Access summary
                </span>
                <span>{accessSummary(row.roles, catalog)}</span>
              </div>
              <div role="cell" className="text-sm text-muted-foreground">
                <span aria-hidden="true" className={NARROW_LABEL}>
                  Last changed
                </span>
                <span>{formatDateTime(row.lastChangedAt)}</span>
              </div>
              <div role="cell" className="@3xl:text-right">
                <span aria-hidden="true" className={NARROW_LABEL}>
                  Actions
                </span>
                <span className="inline-flex items-center gap-1">
                  <IconButton
                    label={`Edit access for ${name}`}
                    icon={<Pencil className="h-4 w-4" />}
                    onClick={() => onEdit(row)}
                  />
                  <Tooltip
                    content={selfAdmin ? SELF_ADMIN_LOCK_REASON : removeLabel}
                  >
                    <IconButton
                      label={removeLabel}
                      showTooltip={false}
                      disabled={selfAdmin}
                      icon={<UserMinus className="h-4 w-4" />}
                      onClick={() => {
                        if (!selfAdmin) {
                          onRemove(row);
                        }
                      }}
                    />
                  </Tooltip>
                  <IconButton
                    label={`View access history for ${name}`}
                    icon={<History className="h-4 w-4" />}
                    onClick={() => onHistory(row)}
                  />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
